import * as React from 'react';
import { observer } from 'mobx-react';
import { TransitionGroup } from 'react-transition-group';

import { IPayerOnboardingMainViewModel } from '../main/payer-onboarding-main-view-model';
import { ModalType } from './modal-type';
import { HelpModalContent, HelpModalHeader } from './help-modal/help-modal';
import { OnboardingInfoModalContent, OnboardingInfoModalHeader } from './onboarding-info-modal/onboarding-info-modal';
import { PayerOnboardingPage } from '../pages';
import { Modal, ModalHeader, ModalContentsHtml } from '../../WebGiving/components/modal/modal';
import { PayerOnboardingPushpayInfo, PayerOnboardingMerchantInfo} from '../payer-onboarding-generated';

@observer
export class ModalCommodore extends React.Component<{
		vm: IPayerOnboardingMainViewModel,
		onError: (error: any, customData?: any) => void,
	}> {
	private documentActiveElement: Element | null;

	render() {
		const { vm } = this.props;
		const { viewData: { PushpayInfo, MerchantInfo }, raiseAction } = vm;
		let content: React.ReactElement<any>[] | null = null;

		switch (vm.visibleModal) {
			case ModalType.NeedHelp:
				content = [
					<HelpModalHeader />,
					<HelpModalContent
						showResendSecurityCode={vm.currentPageViewModel.type === PayerOnboardingPage.ConfirmMobileNumber}
						resendCodeDisabled={vm.resendCodeDisabled.get()}
						onRequestClose={this.handleClose}
						raiseAction={raiseAction}
						pushpayInfo={PushpayInfo} />,
				];
				break;

			case ModalType.LearnMore:
				content = [
					<OnboardingInfoModalHeader />,
					<OnboardingInfoModalContent merchantInfo={MerchantInfo}/>,
				];
				break;

			case ModalType.MerchantPrivacy:
				content = MerchantPrivacy(MerchantInfo);
				break;

			case ModalType.MerchantTerms:
				content = MerchantTerms(MerchantInfo);
				break;

			case ModalType.PushpayPrivacy:
				content = PushpayPrivacy(PushpayInfo);
				break;

			case ModalType.PushpayTerms:
				content = PushpayTerms(PushpayInfo);
				break;

			case null:
				break;

			default:
				const unsupported: never = vm.visibleModal;
				throw new Error(`ModalType ${unsupported} is unsupported`);
		}

		if (content) {
			// ensure keys are assigned
			content = content.map((x, i) => React.cloneElement(x, { key: i }));
		}

		return (
			<TransitionGroup>
				{content && <Modal key={typeof vm.visibleModal !== 'number' ? '' : vm.visibleModal}
					onClose={this.handleClose}
					onEnter={this.setDocumentActiveElement}
					onExited={this.returnFocus}>
					{content}
				</Modal>}
			</TransitionGroup>
		);
	}

	componentDidMount() {
		this.setupGlobalEventHandlers();
	}

	componentDidUpdate() {
		this.setupGlobalEventHandlers();
	}

	componentDidCatch(error: any, info: React.ErrorInfo) {
		this.props.vm.closeModal();
		this.props.onError(error, info);
	}

	private setupGlobalEventHandlers() {
		const anyModalVisible = typeof this.props.vm.visibleModal === 'number';

		if (anyModalVisible) {
			document.body.style.overflow = 'hidden';
			document.documentElement.style.overflow = 'hidden';
			document.body.addEventListener('touchmove', this.preventTouchMove, false);
			document.body.addEventListener('keydown', this.keyDown);
		} else {
			document.body.style.overflow = '';
			document.documentElement.style.overflow = '';
			document.body.removeEventListener('touchmove', this.preventTouchMove, false);
			document.body.removeEventListener('keydown', this.keyDown);
		}
	}

	private keyDown = (evt: any) => {
		if ((evt.key === 'Escape' || evt.keyCode === 27)) {
			this.handleClose();
		}
	}

	private preventTouchMove(ev: TouchEvent) {
		ev.preventDefault();
	}

	private handleClose = () => {
		this.props.vm.closeModal();
	}

	private setDocumentActiveElement = () => {
		this.documentActiveElement = document.activeElement;
	}

	private returnFocus = () => {
		if (this.documentActiveElement && this.documentActiveElement instanceof HTMLElement) {
			this.documentActiveElement.focus();
		}
	}
}

export const PushpayPrivacy = ({ PrivacyPolicy }: PayerOnboardingPushpayInfo) => {
	return [
		<ModalHeader key="modal-header" text="Privacy Policy" />,
		<ModalContentsHtml key="modal-content" className="brand-html-content" contentBase64={PrivacyPolicy} />,
	];
};

export const MerchantPrivacy = ({ TradingName, PrivacyPolicy }: PayerOnboardingMerchantInfo) => {
	return [
		<ModalHeader key="modal-header" text={`Privacy Policy for ${TradingName}`} />,
		<ModalContentsHtml key="modal-content" className="brand-html-content" contentBase64={PrivacyPolicy} />,
	];
};

export const PushpayTerms = ({ TermsAndConditions }: PayerOnboardingPushpayInfo) => {
	return [
		<ModalHeader key="modal-header" text="Terms and Conditions" />,
		<ModalContentsHtml key="modal-content" className="brand-html-content" contentBase64={TermsAndConditions} />,
	];
};

export const MerchantTerms = ({ TradingName, TermsAndConditions }: PayerOnboardingMerchantInfo) => {
	return [
		<ModalHeader key="modal-header" text={`Terms and Conditions for ${TradingName}`} />,
		<ModalContentsHtml key="modal-content" className="brand-html-content" contentBase64={TermsAndConditions} />,
	];
};
